import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { DEFAULT_DESCRIPTION, DEFAULT_IMAGE, SITE_NAME, absoluteUrl } from '../../lib/seo';
import { CONTENT_LANGS, LANG_META, localizedPath, normalizeLang, stripLangPrefix } from '../../lib/contentLocale';

function Seo({
  title,
  description = DEFAULT_DESCRIPTION,
  path = '/',
  image,
  type = 'article',
  jsonLd,
  noIndex = false,
  lang = 'en',
  hreflangLangs,
  xDefaultPath,
}) {
  const resolvedLang = normalizeLang(lang);
  const meta = LANG_META[resolvedLang];
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} - Baby-Led Weaning Made Easy`;
  const canonical = absoluteUrl(path);
  const ogImage = image || DEFAULT_IMAGE;
  const schemas = (Array.isArray(jsonLd) ? jsonLd : [jsonLd]).filter(Boolean);
  const barePath = stripLangPrefix(path);
  const alternateLangs = (hreflangLangs?.length ? hreflangLangs : CONTENT_LANGS).map(normalizeLang);
  const defaultHref = xDefaultPath || (alternateLangs.includes('en') ? barePath : localizedPath(barePath, alternateLangs[0]));

  useEffect(() => {
    document.documentElement.lang = meta.html;
  }, [path, meta.html]);

  return (
    <Helmet>
      <html lang={meta.html} />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {noIndex ? <meta name="robots" content="noindex,follow" /> : <meta name="robots" content="index,follow,max-image-preview:large" />}
      <link rel="canonical" href={canonical} />
      {alternateLangs.map((code) => (
        <link
          key={code}
          rel="alternate"
          hrefLang={LANG_META[code].html}
          href={absoluteUrl(localizedPath(barePath, code))}
        />
      ))}
      <link rel="alternate" hrefLang="x-default" href={absoluteUrl(defaultHref)} />

      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:locale" content={meta.og} />
      {alternateLangs.filter((code) => code !== resolvedLang).map((code) => (
        <meta key={code} property="og:locale:alternate" content={LANG_META[code].og} />
      ))}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      <meta name="pinterest-rich-pin" content="true" />

      {schemas.map((schema, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
}

export default Seo;
